/**
 * Catalog collections (2026-10-10): the 978 supplier products grouped the way
 * people shop, each with a short honest buying note in the owner's voice and
 * a Nano Banana image (/generated-images/v3/shop-*). Pages: /shop/<slug>
 * (ShopPage with a collection). Product pages stay noindex; these pages carry
 * our own text, so they are in the sitemap.
 */
export interface ShopCollection {
  slug: string;
  title: string;
  short: string;
  image: string;
  categories: string[];
  intro: string;
  links: { to: string; label: string; external?: boolean }[];
}

export const COLLECTIONS: ShopCollection[] = [
  {
    slug: 'laptops',
    title: 'מחשבים ניידים',
    short: 'לעבודה, ללימודים ולבית',
    image: 'shop-laptops',
    categories: ['מחשבים ניידים'],
    intro: 'מחשב נייד בוחרים לפי מה שעושים איתו. לעבודה ולימודים מספיקים מעבד בינוני, 16GB זיכרון וכונן SSD של 512GB; לעריכת וידאו ולגיימינג צריך יותר. המחשב מגיע מותקן ומוגדר, מוכן לשימוש, עם אחריות יצרן — והטיפול באחריות דרך המעבדה בנטור. לא בטוחים מה מתאים? ענו על ארבע שאלות בבוחר המחשב, או שאלו בוואטסאפ לפני שמזמינים.',
    links: [
      { to: '/laptop-quiz/', label: 'בוחר המחשב הנייד', external: true },
      { to: '/guides/laptop-buying', label: 'המדריך לקניית מחשב נייד' },
    ],
  },
  {
    slug: 'desktops',
    title: 'מחשבים שולחניים ומיני PC',
    short: 'משרד, בית ועסק',
    image: 'shop-desktops',
    categories: ['מחשבים'],
    intro: 'למשרד, מיני PC מספיק. הוא קטן, שקט ותופס מקום של ספר. מחשב שמוזמן דרך המעבדה מגיע מותקן ומוגדר, מוכן לשימוש, עם אחריות יצרן — והטיפול באחריות דרך המעבדה בנטור. למחשב גיימינג או לעבודה כבדה — עדיף להרכיב בהתאמה אישית, ולשלם רק על מה שצריך.',
    links: [{ to: '/build-pc', label: 'בניית מחשב בהתאמה אישית' }],
  },
  {
    slug: 'monitors',
    title: 'מסכים',
    short: 'למשרד, לעבודה ולגיימינג',
    image: 'shop-monitors',
    categories: ['מסכים'],
    intro: 'בוחרים לפי גודל, רזולוציה וקצב רענון. למשרד מסך 24 אינץ\' Full HD מספיק לרוב; לעבודה עם הרבה חלונות — 27 אינץ\' ברזולוציה גבוהה יותר; לגיימינג — קצב רענון של 144Hz ומעלה. יש כאן גם תושבות למסכים.',
    links: [{ to: '/blog/glossy-matte-ips-oled-screen-guide', label: 'מבריק או מט, IPS או OLED' }],
  },
  {
    slug: 'peripherals',
    title: 'ציוד היקפי',
    short: 'מקלדות, עכברים, אוזניות ומצלמות',
    image: 'shop-peripherals',
    categories: ['ציוד היקפי'],
    intro: 'הדברים שנוגעים בהם כל היום. לעבודה מהבית שווה להשקיע במצלמת רשת ובאוזניות עם מיקרופון טוב — שומעים ורואים אתכם אחרת לגמרי. מקלדת ועכבר אלחוטיים בסט אחד חוסכים כבלים על השולחן. ספרו מה כבר יש לכם, ונגיד מה שווה להחליף.',
    links: [{ to: '/tools/camera-mic-test/', label: 'בדיקת מצלמה ומיקרופון', external: true }],
  },
  {
    slug: 'networking',
    title: 'רשת ו־WiFi',
    short: 'WiFi לכל הבית, ורשת לעסק',
    image: 'shop-networking',
    categories: ['רשתות ותקשורת'],
    intro: 'WiFi חלש לא דורש נתב יקר יותר. כשהוא לא מגיע לכל החדרים, לרוב הפתרון הוא מערכת Mesh או נקודת גישה נוספת. לעסקים יש כאן מתגים, POE ואבטחת רשת. ספרו לנו כמה חדרים וקומות, ונתאים את מה שצריך באמת.',
    links: [],
  },
  {
    slug: 'storage',
    title: 'אחסון וגיבוי',
    short: 'SSD, כוננים חיצוניים ו־NAS',
    image: 'shop-storage',
    categories: ['אחסון'],
    intro: 'SSD פנימי מאיץ מחשב ישן. כונן חיצוני או NAS — לגיבוי, כי לפחות עותק אחד של הקבצים החשובים צריך להיות מחוץ למחשב. רוצים שנתקין? שדרוג ל־SSD במעבדה כולל העברת המערכת והקבצים.',
    links: [
      { to: '/slow-computer', label: 'מחשב איטי — שדרוג ל־SSD' },
      { to: '/blog/proper-backup-how-not-to-lose-your-files', label: 'גיבוי נכון' },
    ],
  },
  {
    slug: 'components',
    title: 'רכיבים ושדרוגים',
    short: 'כרטיסי מסך, זיכרון, מעבדים',
    image: 'shop-components',
    categories: ['רכיבי מחשב'],
    intro: 'לשדרוג או לבניית מחשב. לפני שקונים רכיב, כדאי לוודא שהוא מתאים ללוח האם ולספק הכוח — אם לא בטוחים, תשאלו לפני שמזמינים. רוצים שנרכיב בשבילכם? יש בניית מחשב בהתאמה אישית.',
    links: [{ to: '/build-pc', label: 'בניית מחשב בהתאמה אישית' }],
  },
];

export const collectionBySlug = (slug?: string) => COLLECTIONS.find((c) => c.slug === slug);

/** Kits: no fixed products — the visitor says what they have, Benayahu quotes the rest. */
export const KITS = [
  {
    title: 'פינת עבודה מהבית',
    text: 'מסך, מקלדת ועכבר אלחוטיים, מצלמת רשת ואוזניות. ספרו מה כבר יש לכם — ונרכיב הצעה לכל השאר.',
    image: 'shop-wfh',
    message: 'היי בניהו, אשמח להצעה לפינת עבודה מהבית. מה שכבר יש לי: ',
  },
  {
    title: 'WiFi לכל הבית',
    text: 'כמה חדרים וקומות? נתאים מערכת Mesh או נקודת גישה — ונגיד גם מה לא צריך לקנות.',
    image: 'shop-networking',
    message: 'היי בניהו, ה־WiFi לא מגיע לכל הבית. מספר חדרים וקומות: ',
  },
  {
    title: 'מחשב גיימינג',
    text: 'מרכיבים בהתאמה אישית — חבילות, מחירים וייעוץ חינם לפני ההזמנה.',
    image: 'shop-components',
    to: '/build-pc',
  },
] as { title: string; text: string; image: string; message?: string; to?: string }[];
